// M9-S3: ProjectDetail'de entity-filtered audit log widget. Eylem cümleleri
// lib/audit-labels.ts tek kaynağından (UX P2 İ2 — AdminDashboard ile aynı harita).
// Sadece `settings.audit_log` yetkisine sahip kullanıcılar (admin) görür.
// /admin/settings/audit?entityType=project&entityId={projectId} → son 10 giriş.

import { useEffect, useState } from "react";
import type { AuditLogResponse } from "@mahir/shared";
import { api } from "../../api/client";
import { ACTIVITY_COPY, describeAuditEntry } from "../../lib/audit-labels";
import { useAuthStore } from "../../stores/auth-store";

interface Props {
  projectId: string;
}

export function ProjectAuditWidget({ projectId }: Props): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const canView = user?.permissions["settings.audit_log"] === true;
  const [data, setData] = useState<AuditLogResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canView) return;
    let aborted = false;
    const qs = new URLSearchParams({
      entityType: "project",
      entityId: projectId,
      pageSize: "10",
      page: "1",
    });
    api<AuditLogResponse>(`/admin/settings/audit?${qs.toString()}`)
      .then((res) => {
        if (!aborted) setData(res);
      })
      .catch((err) => {
        if (!aborted) setError(err instanceof Error ? err.message : ACTIVITY_COPY.loadFailed);
      });
    return () => {
      aborted = true;
    };
  }, [projectId, canView]);

  if (!canView) return null;

  return (
    <div
      className="rounded-lg p-4 mb-4"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
      }}
    >
      <h3 className="text-sm mb-3" style={{ color: "var(--color-text-2)", fontWeight: 600 }}>
        {ACTIVITY_COPY.projectTitle}
      </h3>
      {error ? (
        <p style={{ color: "var(--color-error)", fontSize: "0.8rem" }}>{error}</p>
      ) : !data ? (
        <p style={{ color: "var(--color-text-3)", fontSize: "0.8rem" }}>{ACTIVITY_COPY.loading}</p>
      ) : data.entries.length === 0 ? (
        <p style={{ color: "var(--color-text-3)", fontSize: "0.8rem" }}>{ACTIVITY_COPY.empty}</p>
      ) : (
        <ul className="space-y-2" style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {data.entries.map((e) => {
            const d = describeAuditEntry(e);
            return (
              <li
                key={e.id}
                style={{
                  fontSize: "0.8rem",
                  display: "flex",
                  flexDirection: "column",
                  paddingBottom: 8,
                  borderBottom: "1px dashed var(--color-border)",
                }}
              >
                <div
                  style={{ color: "var(--color-text)", fontWeight: 500 }}
                  title={d.rawCode ?? undefined}
                >
                  {d.label}
                </div>
                {d.detail && (
                  <div
                    style={{
                      color: "var(--color-text-2)",
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.7rem",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {d.detail}
                  </div>
                )}
                <div style={{ color: "var(--color-text-3)", fontSize: "0.72rem" }}>
                  {e.actorName ?? e.actorEmail ?? "—"} ·{" "}
                  {new Date(e.createdAt).toLocaleString("tr-TR", {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {data && data.total > data.entries.length ? (
        <p className="mt-2 text-xs" style={{ color: "var(--color-text-3)" }}>
          {ACTIVITY_COPY.shownOf(data.entries.length, data.total)}
        </p>
      ) : null}
    </div>
  );
}
